import { Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SystemSettingsService } from '@wdx/clmi/utils/services';
import {
    Locale,
    TranslationsService,
    IMeLocale,
    decimalMaskNumberConfigOverride,
} from '@wdx/shared/utils';
import { take } from 'rxjs/operators';

@Component({
    selector: 'clmi-filter-duration',
    templateUrl: './filter-duration.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FilterDurationComponent),
        },
    ],
    standalone: false,
})
export class FilterDurationComponent implements ControlValueAccessor, OnInit {
    @Input() cySelector?: string;

    private translationsService = inject(TranslationsService);
    private dynamicDataService = inject(IMeLocale);

    private businessDayLengthHours = inject(SystemSettingsService).get()
        ?.BusinessDayLengthHours;
    private durationApiToUiHours = 60 * 60;
    private value: number | null = null;
    public decimalMaskNumberConfigOverride = decimalMaskNumberConfigOverride;

    public timeUnits: { value: string; label: string }[] =
        this.businessDayLengthHours && this.businessDayLengthHours > 0
            ? [
                  {
                      value: 'hours',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_HOURS',
                      ),
                  },
                  {
                      value: 'days',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_DAYS',
                      ),
                  },
              ]
            : [
                  {
                      value: 'hours',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_HOURS',
                      ),
                  },
              ];
    public timeUnit =
        this.businessDayLengthHours && this.businessDayLengthHours > 0
            ? this.timeUnits[1]
            : this.timeUnits[0];
    public timeValue: number | null = null;
    public disabled = false;

    onChange = (_value: number | null): void => undefined;
    onTouched = () => undefined;

    ngOnInit(): void {
        this.dynamicDataService
            .getMeLocale$()
            .pipe(take(1))
            .subscribe((locale: Locale | undefined) => {
                this.decimalMaskNumberConfigOverride = {
                    ...decimalMaskNumberConfigOverride,
                    decimal: locale?.numberFormat
                        ?.numberDecimalSeparator as string,
                    thousands: locale?.numberFormat
                        ?.numberGroupSeparator as string,
                };
            });
    }

    writeValue(value: number | null): void {
        this.value = typeof value === 'number' ? value : null;
        this.timeValue = this.convertToUi(this.value);
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    timeValueChange(value: number): void {
        this.timeValue = value;
        this.value = this.convertToApi(value);
        this.onTouched();
        this.onChange(this.value);
    }

    timeUnitChange(): void {
        this.timeValue = this.convertToUi(this.value);
        this.onTouched();
    }

    convertToUi(value: number | null): number | null {
        if (typeof value !== 'number') {
            return null;
        }

        if (this.timeUnit?.value === 'days' && this.businessDayLengthHours) {
            return (
                (value || 0) /
                (this.durationApiToUiHours * this.businessDayLengthHours)
            );
        }

        return (value || 0) / this.durationApiToUiHours;
    }

    private convertToApi(value: number): number | null {
        if (typeof value !== 'number') {
            return null;
        }

        if (this.timeUnit?.value === 'days' && this.businessDayLengthHours) {
            return (
                (value || 0) *
                this.durationApiToUiHours *
                this.businessDayLengthHours
            );
        }

        return (value || 0) * this.durationApiToUiHours;
    }
}
